import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider, Grid, LinearProgress, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import dayjs from 'dayjs';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { AppCard } from '../../../shared/components/ui';
import {
  IAndamentoSped, IEtapaSped, IPendenciaSped, SpedService, TNivelPendenciaSped, TStatusEtapaSped,
} from '../../../shared/services/api/Fiscal/SpedService';

// Tela do SPED Fiscal (EFD ICMS/IPI): explica o que é, gera o arquivo do mês
// mostrando cada etapa ao vivo (o backend roda em segundo plano e a tela
// consulta o andamento a cada meio segundo — ver SpedService) e lista o que
// precisa ser corrigido, com um botão que abre a tela certa do SIAC.

const fmtMoeda = (v: number) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ICONE_ETAPA: Record<TStatusEtapaSped, React.ReactNode> = {
  aguardando: <RadioButtonUncheckedRoundedIcon sx={{ color: 'text.disabled' }} />,
  executando: <CircularProgress size={22} thickness={5} />,
  ok: <CheckCircleRoundedIcon color="success" />,
  aviso: <WarningAmberRoundedIcon color="warning" />,
  erro: <ErrorRoundedIcon color="error" />,
};

const INFO_NIVEL: Record<TNivelPendenciaSped, { rotulo: string; cor: 'error' | 'warning' | 'info'; icone: React.ReactNode; explicacao: string }> = {
  erro: { rotulo: 'Erros', cor: 'error', icone: <ErrorRoundedIcon color="error" />, explicacao: 'Precisam ser corrigidos — o programa da Receita vai recusar o arquivo.' },
  aviso: { rotulo: 'Avisos', cor: 'warning', icone: <WarningAmberRoundedIcon color="warning" />, explicacao: 'O arquivo é gerado, mas confira — pode faltar algo ou precisar de ajuste.' },
  info: { rotulo: 'Informações', cor: 'info', icone: <InfoOutlinedIcon color="info" />, explicacao: 'Explicações sobre como o arquivo foi montado. Nada a corrigir.' },
};

// Mês fechado anterior — o SPED é sempre gerado depois que o mês termina.
const mesAnterior = () => dayjs().subtract(1, 'month').format('YYYY-MM');

export const SpedFiscal: React.FC = () => {
  const theme = useTheme();
  const [mes, setMes] = useState(mesAnterior());
  const [finalidade, setFinalidade] = useState<0 | 1>(0);
  const [mostrarExplicacao, setMostrarExplicacao] = useState(true);
  const [andamento, setAndamento] = useState<IAndamentoSped | null>(null);
  const [gerando, setGerando] = useState(false);
  const [erroInicio, setErroInicio] = useState('');
  const [filtroNivel, setFiltroNivel] = useState<TNivelPendenciaSped | 'todos'>('todos');
  const intervaloRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => () => { if (intervaloRef.current) clearInterval(intervaloRef.current); }, []);

  const pararAcompanhamento = () => {
    if (intervaloRef.current) clearInterval(intervaloRef.current);
    intervaloRef.current = undefined;
  };

  const gerar = async () => {
    setErroInicio('');
    setAndamento(null);
    setFiltroNivel('todos');
    setGerando(true);
    setMostrarExplicacao(false);
    const id = await SpedService.iniciar(mes, finalidade);
    if (id instanceof Error) {
      setErroInicio(id.message);
      setGerando(false);
      return;
    }
    pararAcompanhamento();
    // Consulta o andamento até terminar — cada resposta atualiza as etapas e
    // as pendências encontradas até ali.
    intervaloRef.current = setInterval(async () => {
      const resposta = await SpedService.andamento(id);
      if (resposta instanceof Error) {
        pararAcompanhamento();
        setErroInicio(resposta.message);
        setGerando(false);
        return;
      }
      setAndamento(resposta);
      if (resposta.concluido) {
        pararAcompanhamento();
        setGerando(false);
      }
    }, 500);
  };

  const etapas = andamento?.etapas ?? [];
  const pendencias = useMemo(() => andamento?.pendencias ?? [], [andamento]);
  const contagem = useMemo(() => ({
    erro: pendencias.filter((p) => p.nivel === 'erro').length,
    aviso: pendencias.filter((p) => p.nivel === 'aviso').length,
    info: pendencias.filter((p) => p.nivel === 'info').length,
  }), [pendencias]);
  const etapasConcluidas = etapas.filter((e) => ['ok', 'aviso', 'erro'].includes(e.status)).length;
  const progresso = etapas.length ? (etapasConcluidas / etapas.length) * 100 : 0;
  const pendenciasVisiveis = pendencias
    .filter((p) => filtroNivel === 'todos' || p.nivel === filtroNivel)
    .sort((a, b) => ['erro', 'aviso', 'info'].indexOf(a.nivel) - ['erro', 'aviso', 'info'].indexOf(b.nivel));
  const resumo = andamento?.resultado?.resumo;
  const nomeMes = dayjs(`${mes}-01`).isValid() ? dayjs(`${mes}-01`).format('MMMM [de] YYYY') : '';

  const abrirCorrecao = (link: string) => {
    // Nova aba: a análise continua aberta aqui pra conferir o resto.
    window.open(link, '_blank', 'noopener');
  };

  const linhaEtapa = (etapa: IEtapaSped, indice: number) => {
    const ativa = etapa.status === 'executando';
    return (
      <Box key={etapa.id} display="flex" gap={1.5} sx={{ position: 'relative', pb: indice < etapas.length - 1 ? 2 : 0 }}>
        {/* linha vertical ligando as etapas */}
        {indice < etapas.length - 1 && (
          <Box sx={{ position: 'absolute', left: 11, top: 28, bottom: 0, width: 2, bgcolor: etapa.status === 'aguardando' ? 'divider' : alpha(theme.palette.success.main, 0.4) }} />
        )}
        <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25 }}>
          {ICONE_ETAPA[etapa.status]}
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography fontWeight={700} color={etapa.status === 'aguardando' ? 'text.disabled' : 'text.primary'}>
            {indice + 1}. {etapa.titulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">{etapa.descricao}</Typography>
          {etapa.detalhe && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5, fontWeight: 600,
                color: etapa.status === 'erro' ? 'error.dark' : etapa.status === 'aviso' ? 'warning.dark' : ativa ? 'primary.dark' : 'success.dark',
              }}
            >
              {etapa.detalhe}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const cartaoPendencia = (p: IPendenciaSped) => {
    const info = INFO_NIVEL[p.nivel];
    const cor = theme.palette[info.cor].main;
    return (
      <Box
        key={p.id}
        sx={{
          display: 'flex', gap: 1.5, p: 1.5, borderRadius: 2,
          border: `1px solid ${alpha(cor, 0.35)}`, bgcolor: alpha(cor, 0.05),
        }}
      >
        <Box sx={{ mt: 0.25, flexShrink: 0 }}>{info.icone}</Box>
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={700}>{p.titulo}</Typography>
            <Chip label={p.area} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{p.detalhe}</Typography>
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{p.nivel === 'info' ? 'Observação: ' : 'Como corrigir: '}</Box>
            {p.comoCorrigir}
          </Typography>
        </Box>
        {p.link && (
          <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
            <Button size="small" variant="outlined" color={info.cor} endIcon={<OpenInNewRoundedIcon fontSize="small" />} onClick={() => abrirCorrecao(p.link as string)}>
              Corrigir
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const itemResumo = (rotulo: string, valor: React.ReactNode, destaque?: string) => (
    <Grid item xs={6} sm={4} md={3}>
      <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
      <Typography fontWeight={800} color={destaque}>{valor}</Typography>
    </Grid>
  );

  return (
    <LayoutBaseDePagina titulo="SPED Fiscal" subtitulo="Gere o arquivo da EFD ICMS/IPI do mês e veja o que precisa ser corrigido antes de enviar">
      <Box display="flex" flexDirection="column" gap={2} sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
        {/* O que é / como funciona */}
        <AppCard sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ cursor: 'pointer' }} onClick={() => setMostrarExplicacao((v) => !v)}>
            <DescriptionRoundedIcon color="primary" />
            <Typography fontWeight={800} flex={1}>O que é o SPED Fiscal e como funciona aqui</Typography>
            {mostrarExplicacao ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </Box>
          <Collapse in={mostrarExplicacao}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              O SPED Fiscal (EFD ICMS/IPI) é um arquivo mensal com <b>todas as notas fiscais de entrada e saída</b> da empresa e o
              <b> cálculo do ICMS do mês</b>. Ele é validado, assinado e enviado ao fisco pelo programa da Receita chamado <b>PVA EFD ICMS/IPI</b> —
              normalmente pelo contador. O SIAC monta esse arquivo a partir dos XMLs das notas e dos cadastros.
            </Typography>
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {[
                { n: 1, t: 'Escolha o mês', d: 'O SPED é sempre de um mês já encerrado.' },
                { n: 2, t: 'O SIAC analisa e gera', d: 'Cada etapa aparece na tela, com o que foi encontrado.' },
                { n: 3, t: 'Corrija o que for apontado', d: 'Cada problema mostra como resolver e um botão para a tela certa.' },
                { n: 4, t: 'Baixe e envie', d: 'Abra o arquivo no PVA (ou mande ao contador) para validar e transmitir.' },
              ].map((passo) => (
                <Grid item xs={12} sm={6} md={3} key={passo.n}>
                  <Box display="flex" gap={1}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.dark', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.85rem' }}>
                      {passo.n}
                    </Box>
                    <Box>
                      <Typography fontWeight={700} fontSize="0.9rem">{passo.t}</Typography>
                      <Typography variant="caption" color="text.secondary">{passo.d}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              O tratamento do ICMS segue o <b>regime tributário (CRT)</b> do cadastro da empresa. Empresas do <b>Simples Nacional</b>, em regra,
              são dispensadas do SPED Fiscal (só entregam se o estado exigir) — confirme com o contador.
            </Alert>
          </Collapse>
        </AppCard>

        {/* Parâmetros */}
        <AppCard sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                size="small"
                type="month"
                label="Mês do SPED"
                value={mes}
                onChange={(e) => setMes(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format('YYYY-MM') }}
                disabled={gerando}
                helperText={nomeMes ? `Notas de ${nomeMes}` : ' '}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={5}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>Tipo de envio</Typography>
              <ToggleButtonGroup
                exclusive size="small" value={finalidade} disabled={gerando}
                onChange={(_, v) => { if (v !== null) setFinalidade(v); }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
              >
                <Tooltip title="Primeiro envio do mês"><ToggleButton value={0}>Original</ToggleButton></Tooltip>
                <Tooltip title="Substitui um arquivo do mesmo mês que já foi enviado com erro"><ToggleButton value={1}>Retificadora</ToggleButton></Tooltip>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
              <Button
                variant="contained" size="large" onClick={gerar} disabled={gerando || !mes}
                startIcon={gerando ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                sx={{ width: { xs: '100%', md: 'auto' }, fontWeight: 700, textTransform: 'none' }}
              >
                {gerando ? 'Analisando...' : 'Analisar e gerar'}
              </Button>
            </Grid>
          </Grid>
          {erroInicio && <Alert severity="error" sx={{ mt: 2 }}>{erroInicio}</Alert>}
        </AppCard>

        {andamento && (
          <Grid container spacing={2}>
            {/* Passo a passo */}
            <Grid item xs={12} md={5}>
              <AppCard sx={{ p: { xs: 2, sm: 2.5 }, height: '100%' }}>
                <Typography fontWeight={800} sx={{ mb: 0.5 }}>Passo a passo</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  {andamento.concluido ? 'Análise concluída.' : 'Acompanhe o que o SIAC está fazendo agora.'}
                </Typography>
                <LinearProgress variant="determinate" value={progresso} sx={{ height: 6, borderRadius: 3, mb: 2 }} />
                {etapas.map(linhaEtapa)}
                {andamento.erroFatal && <Alert severity="error" sx={{ mt: 2 }}>{andamento.erroFatal}</Alert>}
              </AppCard>
            </Grid>

            {/* Inconsistências */}
            <Grid item xs={12} md={7}>
              <AppCard sx={{ p: { xs: 2, sm: 2.5 }, height: '100%' }}>
                <Typography fontWeight={800} sx={{ mb: 0.5 }}>O que foi encontrado</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  {pendencias.length === 0
                    ? (andamento.concluido ? 'Nenhum problema encontrado.' : 'Os problemas aparecem aqui conforme a análise avança.')
                    : 'Corrija os erros primeiro. O botão "Corrigir" abre a tela certa numa nova aba.'}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Chip label={`Todos (${pendencias.length})`} onClick={() => setFiltroNivel('todos')} color={filtroNivel === 'todos' ? 'primary' : 'default'} variant={filtroNivel === 'todos' ? 'filled' : 'outlined'} />
                  {(['erro', 'aviso', 'info'] as TNivelPendenciaSped[]).map((nivel) => (
                    <Tooltip key={nivel} title={INFO_NIVEL[nivel].explicacao}>
                      <Chip
                        icon={<Box component="span" sx={{ display: 'flex', '& svg': { fontSize: 18 } }}>{INFO_NIVEL[nivel].icone}</Box>}
                        label={`${INFO_NIVEL[nivel].rotulo} (${contagem[nivel]})`}
                        onClick={() => setFiltroNivel(nivel)}
                        color={filtroNivel === nivel ? INFO_NIVEL[nivel].cor : 'default'}
                        variant={filtroNivel === nivel ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <Box display="flex" flexDirection="column" gap={1} sx={{ maxHeight: { md: 560 }, overflowY: 'auto', pr: { md: 0.5 } }}>
                  {pendenciasVisiveis.map(cartaoPendencia)}
                </Box>
              </AppCard>
            </Grid>
          </Grid>
        )}

        {/* Resultado */}
        {andamento?.concluido && andamento.resultado && resumo && (
          <AppCard sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Alert
              severity={contagem.erro > 0 ? 'error' : contagem.aviso > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              action={(
                <Button
                  color="inherit" variant="outlined" size="small" startIcon={<DownloadRoundedIcon />}
                  onClick={() => SpedService.baixarArquivo(andamento.resultado!.nomeArquivo, andamento.resultado!.arquivoBase64)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Baixar arquivo
                </Button>
              )}
            >
              {contagem.erro > 0
                ? `Arquivo gerado, mas com ${contagem.erro} erro(s): o programa da Receita vai recusar até que sejam corrigidos. Corrija e gere de novo.`
                : contagem.aviso > 0
                  ? `Arquivo gerado. Confira os ${contagem.aviso} aviso(s) antes de enviar.`
                  : 'Arquivo gerado sem problemas encontrados.'}
            </Alert>

            <Typography fontWeight={800} sx={{ mb: 1 }}>Resumo de {resumo.periodo}</Typography>
            <Grid container spacing={2}>
              {itemResumo('Regime', resumo.regime)}
              {itemResumo('NF-e emitidas', resumo.notasSaida)}
              {itemResumo('NFC-e emitidas', resumo.nfce)}
              {itemResumo('Canceladas', resumo.notasSaidaCanceladas)}
              {itemResumo('Notas de compra', resumo.notasEntrada)}
              {itemResumo('Clientes/fornecedores', resumo.participantes)}
              {itemResumo('Produtos', resumo.produtos)}
              {itemResumo('Linhas do arquivo', resumo.linhas)}
              {itemResumo('Total de vendas', fmtMoeda(resumo.valorSaidas))}
              {itemResumo('Total de compras', fmtMoeda(resumo.valorEntradas))}
              {itemResumo('ICMS das vendas (débitos)', fmtMoeda(resumo.icmsDebitos))}
              {itemResumo('ICMS das compras (créditos)', fmtMoeda(resumo.icmsCreditos))}
              {itemResumo('ICMS a recolher', fmtMoeda(resumo.icmsARecolher), resumo.icmsARecolher > 0 ? 'error.main' : undefined)}
              {itemResumo('Saldo credor', fmtMoeda(resumo.saldoCredor), resumo.saldoCredor > 0 ? 'success.main' : undefined)}
              {itemResumo('Versão do leiaute', resumo.versaoLeiaute)}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography fontWeight={800} sx={{ mb: 1 }}>Próximos passos</Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5, color: 'text.secondary', '& li': { mb: 0.5 } }}>
              <li>Baixe o arquivo (<b>{andamento.resultado.nomeArquivo}</b>).</li>
              <li>Abra o <b>PVA EFD ICMS/IPI</b> (programa gratuito da Receita) e importe o arquivo — ou envie ao seu contador.</li>
              <li>Clique em <b>Validar</b>: o PVA confere tudo e mostra eventuais erros restantes (ex.: registros que o SIAC indicou para completar).</li>
              <li>Com o arquivo válido, <b>assine</b> com o certificado digital e <b>transmita</b>.</li>
            </Box>
          </AppCard>
        )}
      </Box>
    </LayoutBaseDePagina>
  );
};
